import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import styled from "styled-components";
import { Eth } from "styled-icons/crypto";

import { Wallet } from "@styled-icons/ionicons-solid";
import { useWeb3React } from "@web3-react/core";

import Logo from "../../assets/gg-logo.png";
import { useGnarlyGoatsContractsState } from "../../hooks/useGnarlyGoatsContracts";
import { etherscanUrl } from "../../hooks/useHelpers";
import { Text } from "../../theme/components";
import Row from "../Row";

const StyledEthLogo = styled(Eth)`
	:hover {
		cursor: pointer;
		user-select: none;
	}
`

const HeaderFrame = styled.div`
	display: grid;
	grid-template-columns: 1fr 2fr 1fr;
	width: 100%;
	min-height: 100%;
	top: 0;
	position: relative;
	padding: 1rem;
	place-items: center;

	${({ theme }) => theme.mediaWidth.upToMedium`
    	padding:  1rem;
    	grid-template-columns: 1fr 5fr;
  	`};

	${({ theme }) => theme.mediaWidth.upToExtraSmall`
    	padding: 1rem;
		grid-template-columns: 1fr;
  	`}
`

const NavLinks = styled(Row)`
	justify-self: center;
	background-color: ${({ theme }) => theme.bg2};
	width: fit-content;
	padding: 4px;
	border-radius: 16px;
	display: grid;
	grid-auto-flow: column;
	grid-space: 10px;
	overflow: auto;
	justify-content: center;
	${({ theme }) => theme.mediaWidth.upToMedium`
		justify-self: flex-end;
	`};
	${({ theme }) => theme.mediaWidth.upToExtraSmall`
		width: -moz-available;
		width: -webkit-fill-available;
    	justify-self: center;
	`};
`

const activeclassname = 'ACTIVE'

const StyledNavLink = styled(NavLink).attrs({
	activeclassname,
})<{
	owner?: boolean
}>`
	${({ theme }) => theme.flexRowNoWrap}
	align-items: left;
	border-radius: 3rem;
	outline: none;
	cursor: pointer;
	text-decoration: none;
	color: ${({ owner, theme }) => (owner ? theme.owner1 : theme.text2)};
	font-size: 18px;
	font-weight: 500;
	padding: 8px 12px;
	word-break: break-word;
	overflow: hidden;
	white-space: nowrap;

	:hover {
		color: ${({ owner, theme }) => (owner ? theme.white : theme.primary1)};
	}
`

export const StyledMenuButton = styled.button`
	position: relative;
	width: 100%;
	height: 100%;
	border: none;
	background-color: transparent;
	margin: 0;
	padding: 0;
	height: 35px;
	font-weight: 600;
	color: ${({ theme }) => theme.text1};
	background-color: ${({ theme }) => theme.bg3};
	margin-left: 8px;
	padding: 0.15rem 0.5rem;
	border-radius: 0.5rem;

	:hover,
	:focus {
		cursor: pointer;
		outline: none;
		background-color: ${({ theme }) => theme.bg4};
	}

	svg {
		margin-top: 2px;
	}
	> * {
		stroke: ${({ theme }) => theme.text1};
	}
`

const Icon = styled.div`
	padding: 6px;
	margin: 0 0 0 1rem;
	// filter: drop-shadow(15px 15px 15px ${({ theme }) => theme.primary5});
	transition: transform 0.3s ease;
	:hover {
		cursor: pointer;
		transform: rotate(-5deg);
	}
`

const AccountWrapper = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-self: flex-end;
	width: 100%;
	${({ theme }) => theme.mediaWidth.upToMedium`
		flex-direction: row;
		justify-content: space-between;
		justify-self: center;
		max-width: 960px;
		padding: 1rem;
		position: fixed;
		bottom: 0px;
		left: 0px;
		z-index: 99;
		height: 72px;
  `};
`

const Account = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	max-width: fit-content;
	padding: 0px 8px;
	border-radius: 10rem;
	box-shadow: 5px 5px 20px ${({ theme }) => theme.black},
		-5px -5px 20px ${({ theme }) => theme.bg1};
	background-image: ${({ theme }) =>
		`linear-gradient(120deg, ${theme.primary1} 0%, ${theme.black} 100%)`};
`

const Header = (props: any) => {
	const { isOwner } = useGnarlyGoatsContractsState()
	const { account, active } = useWeb3React()

	const [prettyAddress, setPrettyAddress] = useState<string>()

	useEffect(() => {
		if (account) {
			setPrettyAddress(
				`${account.slice(0, 5)}...${account.slice(
					account.length - 4,
					account.length
				)}`
			)
		}
	}, [account])

	return (
		<HeaderFrame>
			<StyledNavLink id={`home-nav-link`} to={'/'}>
				<Icon>
					<img width={'165px'} src={Logo} alt="logo" />
				</Icon>
			</StyledNavLink>

			<NavLinks>
				<StyledNavLink id={`provenance-nav-link`} to={'/provenance'}>
					Provenance
				</StyledNavLink>

				{active && !!account && (
					<StyledNavLink id={`members-nav-link`} to={'/members'}>
						Members
					</StyledNavLink>
				)}

				<StyledNavLink id={`about-nav-link`} to={`/about`}>
					About
				</StyledNavLink>
			</NavLinks>

			{active && !!account && (
				<AccountWrapper>
					<Account>
						<StyledEthLogo
							size={28}
							onClick={() => {
								navigator.clipboard.writeText(account)
								toast.success(`Address Copied`)
							}}
						/>

						{/* only the contract's owner should be able to see this nav link */}
						{active && !!account && isOwner === true ? (
							<StyledNavLink owner id={`owner-nav-link`} to={'/owner'}>
								{prettyAddress}
							</StyledNavLink>
						) : (
							<Text indent>{prettyAddress}</Text>
						)}

						<StyledNavLink
							id={`disconnect-nav-link`}
							to={'/'}
							onClick={() => {
								window.open(etherscanUrl(account), '_blank')
							}}>
							<Wallet size={28} />
						</StyledNavLink>
					</Account>
				</AccountWrapper>
			)}
		</HeaderFrame>
	)
}

export default Header
